import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { ISavingsDepositRecord } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface ISearchDepositsRequest {
  pageNumber?: number;
  pageSize?: number;
  searchParams?: {
    debitAccount?: string;
    creditAccount?: string;
  };
}

interface ISearchDepositsResponse {
  status?: boolean;
  message?: string;
  totalCount?: number;
  data?: ISavingsDepositRecord[];
  errors?: IApiResponseError[];
}

const initialState: IApiState<ISearchDepositsResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const searchDepositsAsync = createAsyncThunk(
  'savings/deposits/search',
  async (
    { pageNumber = 1, pageSize = 10, searchParams = {} }: ISearchDepositsRequest,
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.post<ISearchDepositsResponse>(
        '/savings/deposits/search',
        { pageNumber, pageSize, searchParams },
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const searchDepositsSlice = createSlice({
  name: 'savings/deposits/search',
  initialState,
  reducers: {
    resetSearchDeposits(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchDepositsAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(searchDepositsAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ISearchDepositsResponse;
      })
      .addCase(searchDepositsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetSearchDeposits } = searchDepositsSlice.actions;

export default searchDepositsSlice.reducer;
